import { ElUploadInternalFileDetail } from 'element-ui/types/upload'
import { readFileAsDataURL } from '@com/utils/file-reader'
import store from '@ps/store'
import { getImageSize } from '@com/image'
import _ from 'lodash'
import validator from '@com/utils/file-validator'
import { maxXlzSize } from '@ps/utils/config'
import i18n from '@ps/lang'
import { projectBasicPath } from '@ps/workbench/config'
import router from '@ps/router'
import qc from 'qc'

type ValidResultListTypes = {
  valid: boolean
  errors: {
    valid: boolean
    message?: string
    rule?: string
  }[]
}[]

export async function validate(
  fileList: ElUploadInternalFileDetail[],
  zipfile?: File,
): Promise<{
  valid: boolean
  errors?: {
    rule: string
    message: string
  }[] // 整体的错误信息
  validResultList: ValidResultListTypes // 所有的校验结果信息
}> {
  const res = await Promise.all(fileList.map((file) => getImageSize(file.raw)))
  if (_.uniqWith(res, _.isEqual).length > 1) {
    return {
      valid: false,
      errors: [
        {
          rule: 'dimension',
          message: i18n.t('ps.uoload.subTips.seqSizeTip').toString(),
        },
      ],
      validResultList: [],
    }
  }

  const validResultList: ValidResultListTypes = []

  if (zipfile) {
    validResultList.push(
      validator([
        {
          validate: ({ file }) => {
            if (file.size > maxXlzSize * 1024 * 1024) {
              return {
                valid: false,
                message: i18n
                  .t('ps.uoload.subTips.seqMaxSizeTip', {
                    size: maxXlzSize,
                  })
                  .toString(),
                rule: 'size',
              }
            }

            return {
              valid: true,
              message: '',
              rule: '',
            }
          },
          // @ts-ignore
          file: zipfile,
        },
      ]),
    )
  }

  if (validResultList.every((item) => item.valid)) {
    return {
      valid: true,
      validResultList,
    }
  }

  const errors = _.uniq(
    _.flattenDeep(
      validResultList
        .filter((item) => !item.valid)
        .map((item) =>
          item.errors.map((item) => ({
            ...item,
            rule: item.rule || '',
            message:
              item.message ||
              i18n.t('ps.upload.subTips.unknownError').toString(),
          })),
        ),
    ),
  )

  return {
    valid: false,
    errors,
    validResultList,
  }
}

export async function addXlzToProjectStore({
  fileList,
  actionName,
}: {
  fileList: ElUploadInternalFileDetail[]
  actionName: string
}) {
  const { valid, errors } = await validate(fileList)

  if (!valid) {
    throw new Error(
      errors
        ? errors[0]!.message
        : i18n.t('ps.uoload.subTips.seqFormatTip').toString(),
    )
  }

  // 上传到项目资源库
  const filesDataURL = await Promise.all(
    fileList.map((file) => readFileAsDataURL(file.raw)),
  )
  const files = filesDataURL.map((item, index) => {
    return {
      data: item.target.result.replace(/^data:image\/\w+;base64,/, ''),
      name: fileList[index]!.name,
    }
  })
  const res = await store.dispatch(
    'workspace/resourceManager/createComponent',
    {
      category: 'xlz',
      data: {
        name: actionName,
        files,
      },
    },
  )
  if (res.code === 0) return res.data
  throw new Error(res.msg)
}

export async function addXlzToPlayable({
  fileList,
  actionName,
  isAddNode = true,
}: {
  fileList: ElUploadInternalFileDetail[]
  actionName: string
  isAddNode?: boolean
}): Promise<boolean> {
  await store.dispatch('workspace/sequenceManager/addSequenceFrame', {
    fileList,
    actionName,
    isAddNode,
    callback: async ({
      node,
      binList,
    }: {
      node: qc.Node
      binList: string[]
    }) => {
      const fileList = binList.map((bin) => ({
        value: bin,
        src: `${projectBasicPath}${router.currentRoute.query.id}/${bin.replace(
          /\.bin$/i,
          '.png',
        )}`,
      }))
      // originalResource 暂时用项目中的地址代替
      await store.dispatch('workspace/editorConfig/updateTargetNodeConfig', {
        originalResource: fileList,
        resource: fileList,
        node,
      })
    },
  })
  return true
}

// ElUploadInternalFileDetail
export function sortFileList(
  fileList: ElUploadInternalFileDetail[],
): ElUploadInternalFileDetail[] {
  fileList.sort((a, b) => {
    return sortFileName(a.name, b.name)
  })
  return fileList
}

// [string]
export function sortFileSrcList(list: string[]) {
  list.sort((a, b) => {
    return sortFileName(a, b)
  })
  return list
}

export function sortFileName(a: string, b: string) {
  let matchArrA = a.match(/(.*)_(\d+)\.(.*)/)
  let matchArrB = b.match(/(.*)_(\d+)\.(.*)/)
  let numa = null
  let numb = null

  if (matchArrA && matchArrA[2] && matchArrB && matchArrB[2]) {
    numa = Number(matchArrA[2])
    numb = Number(matchArrB[2])
  } else {
    matchArrA = a.match(/(\d+)\.(.*)/)
    matchArrB = b.match(/(\d+)\.(.*)/)
    if (matchArrA && matchArrA[1] && matchArrB && matchArrB[1]) {
      numa = Number(matchArrA[1])
      numb = Number(matchArrB[1])
    }
  }
  if (numa === null || numb === null) {
    return -1
  }
  if (numa < numb) {
    return -1
  }
  return 1
}
